import { useState } from "react";
import { UserCog, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PhoneInput } from "@/components/PhoneInput";

const inputCls = "w-full h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none";

/** Edit an employee's name / email / phone / role. */
export function EmployeeEditDialog({ person, roleOptions, isSelf, canSetAdmin, onClose, onSaved }) {
  const [f, setF] = useState({ name: person.name || "", email: person.email || "", phone: person.phone || "", role: person.role });
  const [busy, setBusy] = useState(false);
  const needsEmail = f.role !== "staff";

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.put(`/officers/${person.id}`, { name: f.name.trim(), email: f.email.trim() || null, phone: f.phone, role: f.role });
      toast.success(`${data.name} updated`);
      onSaved(data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="employee-edit-dialog">
        <form onSubmit={save} className="space-y-4">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><UserCog className="h-5 w-5 text-primary" /> Edit employee</DialogTitle>
            <DialogDescription>Changing the email changes what they sign in with. Passwords and PINs are managed separately.</DialogDescription>
          </DialogHeader>
          <label className="block">
            <span className="block mb-1 text-xs uppercase tracking-widest text-slate-500">Full name</span>
            <input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="employee-edit-name" className={inputCls} />
          </label>
          <label className="block">
            <span className="block mb-1 text-xs uppercase tracking-widest text-slate-500">Role</span>
            <select value={f.role} disabled={isSelf} onChange={(e) => setF({ ...f, role: e.target.value })} data-testid="employee-edit-role" className={`${inputCls} disabled:bg-slate-50`}>
              {roleOptions.filter((r) => r.v !== "admin" || canSetAdmin || person.role === "admin").map((r) => <option key={r.v} value={r.v}>{r.label}</option>)}
            </select>
            {isSelf && <span className="block mt-1 text-[11px] text-slate-400">You can't change your own role.</span>}
          </label>
          <label className="block">
            <span className="block mb-1 text-xs uppercase tracking-widest text-slate-500">Login email {needsEmail ? "" : "(optional)"}</span>
            <input type="email" required={needsEmail} value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="employee-edit-email" className={inputCls} />
          </label>
          <label className="block">
            <span className="block mb-1 text-xs uppercase tracking-widest text-slate-500">Phone (optional)</span>
            <PhoneInput value={f.phone} onChange={(v) => setF({ ...f, phone: v })} placeholder="(214) 555-0100" data-testid="employee-edit-phone" className={inputCls} />
          </label>
          <DialogFooter>
            <button type="button" onClick={onClose} className="h-10 rounded-lg border border-slate-200 bg-white px-4 text-sm font-medium text-slate-700 hover:bg-slate-100">Cancel</button>
            <button type="submit" disabled={busy} data-testid="employee-edit-save" className="h-10 rounded-lg bg-primary px-4 text-sm font-medium text-white inline-flex items-center gap-2 disabled:opacity-60">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
